import { ProductName } from '@pypestream/api-services';
import { logos } from '@pypestream/design-system';

import {
  ManagerContext,
  Project,
  ProjectNew,
} from '../xstate/manager.xstate.types';

export type Product = {
  name: ProductName;
  label: string;
  logo: keyof typeof logos;
  productId: string;
  disabled: boolean;
  url?: string;
  index?: number;
  conditional?: boolean;
  integrated?: boolean;
};

export interface GetProductsProps {
  ctx: ManagerContext;
  project?: Project | ProjectNew;
  isNewProjects?: boolean;
}

interface GetProductsResult {
  products: Product[];
  conditionalProducts: Product[];
  selectedProjectURL: string | undefined;
  hasAvailableProduct: boolean;
}

export interface GetProducts {
  (props: GetProductsProps): GetProductsResult;
}

export const initialTools: Product[] = [
  {
    name: ProductName.Organization,
    label: 'Manager',
    logo: 'manager',
    url: undefined,
    disabled: true,
    index: 1,
    productId: '',
  },
  {
    name: ProductName.AgentAssist,
    label: 'Contact Center',
    logo: 'contactCenter',
    url: undefined,
    conditional: true,
    disabled: true,
    index: 2,
    productId: '',
  },
  {
    name: ProductName.Analytics,
    label: 'Analytics',
    logo: 'analytics',
    url: undefined,
    conditional: true,
    disabled: true,
    index: 3,
    productId: '',
  },
  {
    name: ProductName.ProStudio,
    label: 'Pro Studio',
    logo: 'proStudio',
    url: undefined,
    conditional: true,
    disabled: true,
    index: 4,
    productId: '',
  },
];

export const getProductUrl = (product: ProductName, projectId: string) => {
  switch (product) {
    case ProductName.AgentAssist:
      return `${
        import.meta.env.FE_AGENT_ASSIST_URL
      }/app/accounts/${projectId}/dashboard`;
    case ProductName.Analytics:
      return `${import.meta.env.FE_ANALYTICS_URL}/api/projects/${projectId}/change`;
    case ProductName.ProStudio:
      return `${
        typeof window !== 'undefined' &&
        window.location.hostname.includes('localhost')
          ? 'http://localhost:5174'
          : import.meta.env.FE_PRO_STUDIO_URL
      }/${projectId}`;
    default:
      return '/';
  }
};

/**
 * href for a Project-Details tool card (PSTUDIO-2541). Returns the external cross-app URL when the
 * tool is enabled, else undefined so the card renders as a non-link. Organization/Manager is
 * excluded (getProductUrl returns the SPA root '/', which is not a "tool instance" link here).
 */
export const productToolHref = (
  product: ProductName,
  projectId: string,
  enabled: boolean
): string | undefined => {
  if (!enabled || product === ProductName.Organization) return undefined;
  return getProductUrl(product, projectId);
};

export const getProducts: GetProducts = ({
  project,
  ctx: {
    userInfo,
    allProducts,
    projects: projectsOld,
    projectsNew,
    routes,
    userProjectIds,
    selectedOrgId,
  },
  isNewProjects = false,
}) => {
  const projects = isNewProjects ? projectsNew : projectsOld;

  const initialProjects = isNewProjects
    ? projectsNew
    : projects?.filter((p) =>
        userProjectIds.includes((p as Project).projectId)
      );

  const transformCtxValues = () => ({
    productIds: allProducts?.reduce(
      (acc, { name, id }) => ({ ...acc, [`${name}`]: id }),
      {} as Record<ProductName, string>
    ),
    enabledTools: [
      ...new Set(
        initialProjects
          ?.map((p) => {
            return p?.projectProductSettings
              ?.map((setting) => setting.productId)
              ?.flat();
          })
          ?.flat()
      ),
    ],
  });

  const { productIds, enabledTools } = transformCtxValues();

  const isPypeEmployee = Boolean(userInfo?.isPypestreamEmployee);

  const projectProducts =
    project?.projectProductSettings?.map(({ productId }) => productId) || [];

  const projectId = isNewProjects
    ? (project as ProjectNew)?.id
    : (project as Project)?.projectId;

  const hasAgentAssistRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.agentAssistRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.agentAssistRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  let analyticsProjectId;
  const analyticsProjects =
    initialProjects?.filter((p) => {
      return p?.projectProductSettings?.some(
        (s) => s.productId === productIds?.[ProductName.Analytics]
      );
    }) || [];

  if (analyticsProjects.length === 1) {
    analyticsProjectId = isNewProjects
      ? (analyticsProjects[0] as ProjectNew).id
      : (analyticsProjects[0] as Project).projectId;
  }

  const hasAnalyticsRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.analyticsRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.analyticsRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  let proStudioProjectId;
  const proStudioProjects =
    initialProjects?.filter((p) =>
      p?.projectProductSettings?.some(
        (s) => s.productId === productIds?.[ProductName.ProStudio]
      )
    ) || [];

  if (proStudioProjects.length === 1) {
    proStudioProjectId = isNewProjects
      ? (proStudioProjects[0] as ProjectNew).id
      : (proStudioProjects[0] as Project).projectId;
  }

  const hasProStudioRoles = Boolean(
    projectId
      ? userInfo?.productRoles?.proStudioRolesSource?.some(
          ({ project: p }) => p?.id === projectId
        )
      : userInfo?.productRoles?.proStudioRolesSource?.some(
          ({ project: p }) => p?.accountId === selectedOrgId
        )
  );

  const prostudioURL =
    typeof window !== 'undefined' &&
    window.location.hostname.includes('localhost')
      ? 'http://localhost:5174'
      : import.meta.env.FE_PRO_STUDIO_URL;

  const commonProductsProps: Omit<Product, 'productId'>[] = [
    {
      name: ProductName.Organization,
      label: 'Manager',
      logo: 'manager',
      url: routes.users,
      disabled: false,
      index: 1,
    },
    {
      name: ProductName.AgentAssist,
      label: 'Contact Center',
      logo: 'contactCenter',
      url:
        hasAgentAssistRoles && projectId
          ? getProductUrl(ProductName.AgentAssist, projectId)
          : undefined,
      conditional: true,
      disabled: !hasAgentAssistRoles,
      index: 2,
    },
    {
      name: ProductName.Analytics,
      label: 'Analytics',
      logo: 'analytics',
      url:
        analyticsProjectId || projectId
          ? getProductUrl(
              ProductName.Analytics,
              analyticsProjectId || projectId
            )
          : undefined,
      conditional: true,
      disabled: !hasAnalyticsRoles,
      index: 3,
    },
    {
      name: ProductName.ProStudio,
      label: 'Pro Studio',
      logo: 'proStudio',
      url:
        proStudioProjectId || projectId
          ? getProductUrl(
              ProductName.ProStudio,
              proStudioProjectId || projectId
            )
          : undefined,
      conditional: true,
      disabled: !hasProStudioRoles,
      index: 4,
    },
  ];

  const products: Product[] = commonProductsProps
    .map(({ conditional, disabled, url, name, ...restProps }) => {
      const productId = productIds?.[name] || '';
      const integrated = project ? projectProducts?.includes(productId) : false;
      const isDisabled = !productId
        ? true
        : conditional
          ? disabled ||
            (project ? !integrated : !enabledTools.includes(productId))
          : disabled;

      return {
        ...restProps,
        name,
        url: isDisabled ? undefined : url,
        productId,
        conditional,
        disabled: isDisabled,
        ...(project &&
          conditional && {
            integrated,
          }),
      };
    })
    .sort(
      ({ index: prevIndex }, { index: nextIndex }) =>
        Number(prevIndex) - Number(nextIndex)
    );

  const conditionalProducts: Product[] = products.filter(
    ({ conditional }) => conditional
  );

  const getSelectedProjectURL = () => {
    if (!productIds) return;

    const productUrls = conditionalProducts.filter(({ url }) => url);

    return productUrls.length > 1
      ? undefined
      : productUrls.length === 1
        ? productUrls[0].url
        : products.find(
            ({ productId }) =>
              productId === productIds[ProductName.Organization]
          )?.url;
  };

  const hasAvailableProduct = !project
    ? false
    : project.projectProductSettings?.some((item) =>
        products
          .filter(({ disabled }) => !disabled)
          .some((p) => p.productId === item.productId)
      ) || isPypeEmployee;

  return {
    products,
    conditionalProducts,
    selectedProjectURL: getSelectedProjectURL(),
    hasAvailableProduct,
  };
};
